import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { fetchDisputes, respondDispute } from "./api";

/**
 * Polling leve (15 s) das negociações abertas do iFood. `onNew` dispara quando
 * aparece uma negociação nova (a tela toca o som de alerta). Erros de rede são
 * silenciosos — a tela do Delivery já avisa quando o backend cai.
 */
export default function useIfoodDisputes({ enabled = true, intervalMs = 15000, onNew } = {}) {
  const [disputes, setDisputes] = useState([]);
  const [rejectReasons, setRejectReasons] = useState({});
  const knownIds = useRef(new Set());
  const onNewRef = useRef(onNew);
  onNewRef.current = onNew;

  const refresh = useCallback(async () => {
    try {
      const data = await fetchDisputes();
      const list = Array.isArray(data?.disputes) ? data.disputes : [];
      setDisputes(list);
      if (data?.reject_reasons) setRejectReasons(data.reject_reasons);
      const fresh = list.filter((d) => !knownIds.current.has(d.dispute_id));
      list.forEach((d) => knownIds.current.add(d.dispute_id));
      if (fresh.length > 0 && knownIds.current.size > fresh.length) onNewRef.current?.(fresh);
    } catch (e) {
      /* silencioso */
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    refresh();
    const id = setInterval(refresh, intervalMs);
    return () => clearInterval(id);
  }, [enabled, intervalMs, refresh]);

  const respond = useCallback(
    async (disputeId, action, body) => {
      try {
        await respondDispute(disputeId, action, body);
        toast.success(
          action === "accept" ? "Cancelamento aceito — o iFood vai cancelar o pedido" : action === "reject" ? "Recusa enviada ao iFood" : "Proposta enviada ao iFood",
        );
        setDisputes((prev) => prev.filter((d) => d.dispute_id !== disputeId));
        refresh();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Não foi possível responder à negociação");
        throw e;
      }
    },
    [refresh],
  );

  return { disputes, rejectReasons, respond, refresh };
}
